import { z } from 'zod';

export const dashboardLayouts = ['single', 'two-column', 'two-row', 'two-by-two'] as const;

export const dashboardLayoutSchema = z.enum(dashboardLayouts);

export type DashboardLayout = z.infer<typeof dashboardLayoutSchema>;

export const DASHBOARD_LAYOUT_SLOT_COUNT: Record<DashboardLayout, number> = {
  single: 1,
  'two-column': 2,
  'two-row': 2,
  'two-by-two': 4,
};

// A panel currently holds at most one of: a visualization (FK) or — eventually — a widget.
// When widgets ship, add a nullable `widgetId` column + an XOR check that at most one is set.
export const dashboardPanelDTOSchema = z.object({
  id: z.number().int(),
  organizationId: z.string().min(1),
  dashboardId: z.number().int(),
  position: z.number().int().min(0),
  visualizationId: z.number().int().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type DashboardPanelDTO = z.infer<typeof dashboardPanelDTOSchema>;

export const dashboardDTOSchema = z.object({
  id: z.number().int(),
  organizationId: z.string().min(1),
  loopId: z.number().int(),
  name: z.string().nullable(),
  layout: dashboardLayoutSchema,
  panels: z.array(dashboardPanelDTOSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type DashboardDTO = z.infer<typeof dashboardDTOSchema>;

export function parseDashboardDTO(data: unknown): DashboardDTO {
  return dashboardDTOSchema.parse(data);
}

export function getDashboardDisplayName(dashboard: Pick<DashboardDTO, 'id' | 'name'>): string {
  return dashboard.name?.trim() || `Dashboard (${dashboard.id})`;
}

export const DASHBOARD_MAX_NAME_LENGTH = 64;

// Optional initial-content for a panel slot when creating a dashboard.
// Position must be in range for the chosen layout (validated by the service).
export const dashboardPanelInsertDTOSchema = z.object({
  position: z.number().int().min(0),
  visualizationId: z.number().int().nullable(),
});

export type DashboardPanelInsertDTO = z.infer<typeof dashboardPanelInsertDTOSchema>;

export const dashboardInsertDTOSchema = z
  .object({
    loopId: z.number().int(),
    name: z.string().max(DASHBOARD_MAX_NAME_LENGTH).optional().nullable(),
    layout: dashboardLayoutSchema,
    // If omitted, every panel is seeded empty. If provided, each entry overrides the
    // matching slot (by position). Slots not listed are still seeded empty.
    panels: z.array(dashboardPanelInsertDTOSchema).optional(),
  })
  .strict();

export type DashboardInsertDTO = z.infer<typeof dashboardInsertDTOSchema>;

export const dashboardUpdateDTOSchema = z
  .object({
    name: z.string().max(DASHBOARD_MAX_NAME_LENGTH).optional().nullable(),
    layout: dashboardLayoutSchema.optional(),
  })
  .strict();

export type DashboardUpdateDTO = z.infer<typeof dashboardUpdateDTOSchema>;

// Set or clear a panel's content. Pass `visualizationId: null` to clear.
export const dashboardPanelUpdateDTOSchema = z
  .object({
    visualizationId: z.number().int().nullable(),
  })
  .strict();

export type DashboardPanelUpdateDTO = z.infer<typeof dashboardPanelUpdateDTOSchema>;
